import { create } from 'zustand';
import type { FacetCounts } from './shared';

/** Where the drawer was opened from — drives the footer actions. */
type ActivityFilterDrawerSource = 'default' | 'ai';

type ActivityFilterDrawerStore = {
    isOpen: boolean;
    source: ActivityFilterDrawerSource;
    /** Section id (e.g. `'participants'`, `'budget'`, `'regions'`) to expand on open. */
    section: string | null;
    open: (source?: ActivityFilterDrawerSource, section?: string) => void;
    close: () => void;
    /** Result count of the current filters, so the drawer's button can say
     * how many teambuildings it will show. Written by the results header. */
    totalResults: number | null;
    setTotalResults: (totalResults: number | null) => void;
    /** Results per filter option for the current filters, from the same list
     * the page renders. Written by the results header, read by the sidebar. */
    facetCounts: FacetCounts | null;
    setFacetCounts: (facetCounts: FacetCounts | null) => void;
};

/**
 * Open/close state for the filter drawer, lifted out of `ActivityFilterDrawerButton`
 * so other surfaces (e.g. the AI chat composer's "edit filters" button) can open it.
 * `source` lets the overlay swap its footer: a plain "search" CTA by default, or an
 * "add" CTA when opened from AI (where the filters become prompt chips, not a search).
 */
export const useActivityFilterDrawerStore = create<ActivityFilterDrawerStore>(set => ({
    isOpen: false,
    source: 'default',
    section: null,
    open: (source = 'default', section) => set({ isOpen: true, source, section: section ?? null }),
    close: () => set({ isOpen: false }),
    totalResults: null,
    setTotalResults: totalResults => set({ totalResults }),
    facetCounts: null,
    setFacetCounts: facetCounts => set({ facetCounts }),
}));
